/** What an Auditor's engagement did to a request, said by the screen that made it (Q-03).
 *
 * **A SCREEN matter, never a different API answer.** Since the S1 fix (G-P6-07) a document or a record
 * that exists but is outside an engagement is refused as one that does not exist, byte for byte
 * (`test_engagement_http`), and nothing here asks the server to say more. What the screen knows is
 * the person's *own* terms (`/auth/me`'s `engagement_terms`: a window and an access end) and the
 * status the server gave, and from those two it can say two true things: *this is not here, or it is
 * outside your engagement* (true of both cases, which is the point of the sentence), and *this window
 * was narrowed to the part your engagement covers*.
 */

import type { Me } from "../api/auth";
import { Refused } from "../api/refusal";

/** The one sentence for "not in this workspace, or outside your engagement" - the record page's own,
 * with the noun the screen is about. It confirms nothing: it is true whether the thing exists or not,
 * which is why the S1 fix keeps the two cases indistinguishable at the API. */
export function outsideEngagementSentence(noun: string): string {
  return `There is no such ${noun} in this workspace, or it is outside your engagement.`;
}

/** Whether a failed read is the *answer* a clamp gives rather than a fault: a 404 (not there, or not
 * yours to know about) or a 403 (a role or an engagement refusal). Anything else - a 500, a dropped
 * connection - is still a failure and still says so. */
export function isRefusedAsAbsent(error: unknown): boolean {
  return error instanceof Refused && (error.status === 404 || error.status === 403);
}

/** Whether this person's reads are narrowed by an engagement (a period, or particular companies). */
export function isEngaged(me: Me | null | undefined): boolean {
  return me?.engagement != null;
}

/** What an engagement does to a window the screen is about to ask for.
 *
 * - `null`: nothing. The person is not engaged over a period, or the window is inside the engagement.
 * - `narrowed`: the window straddles the engagement's edge and the server shows the part inside it;
 *   `from`/`to` is that part, which is the server's own clamp (`PeriodScope.clamp`) restated, so the
 *   note and the total beside it cannot disagree about which months they are.
 * - `outside`: nothing of the window is inside the engagement, so there is nothing to show.
 *
 * ISO dates compare as strings, which is why no `Date` is made here. */
export type WindowUnderEngagement =
  | null
  | { kind: "narrowed"; from: string; to: string }
  | { kind: "outside" };

export function windowUnderEngagement(
  me: Me | null | undefined,
  start: string,
  end: string,
): WindowUnderEngagement {
  if (me?.engagement !== "period") return null;
  const first = me.engagement_terms?.period_start ?? null;
  const last = me.engagement_terms?.period_end ?? null;
  if (first === null && last === null) return null;
  const from = first !== null && first > start ? first : start;
  const to = last !== null && last < end ? last : end;
  if (from > to) return { kind: "outside" };
  return from === start && to === end ? null : { kind: "narrowed", from, to };
}
